// Persistent top-bar chip that shows the active account context — or an
// amber pulsing warning when none is selected. Clicking the chip opens a
// searchable dropdown anchored below it (not a centered modal — the user
// expects a context switcher to feel like a menu, not an overlay).
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertTriangle, Building, ChevronDown, MapPin, Search, X } from "lucide-react";
import { useApi } from "@/hooks/useApi";
import { cn } from "@/lib/utils";
import { useActiveAccount } from "@/state/accountContext";
import { Hint } from "@/components/help/InfoTip";

type AccountRow = {
  id: number; name: string; city: string | null; state: string | null; status: string;
  owner_name: string | null; company: string | null; ghl_pipeline_id?: string | null;
};

// The business line: prefer the company, else the owner — the bit that
// tells you WHICH business this account is, distinct from the record name.
function businessOf(r: { company: string | null; owner_name: string | null; name: string }): string | null {
  const c = (r.company ?? "").trim();
  if (c && c.toLowerCase() !== r.name.trim().toLowerCase()) return c;
  const o = (r.owner_name ?? "").trim();
  if (o && o.toLowerCase() !== r.name.trim().toLowerCase()) return o;
  return null;
}

// Re-exported here so AppShell doesn't need two imports for the same concept.
export { useActiveAccount } from "@/state/accountContext";

export function AccountContextBanner() {
  const nav = useNavigate();
  const { activeAccountId: activeId, setActiveAccountId } = useActiveAccount();
  const [pickerOpen, setPickerOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const { data: bundle } = useApi<{ account: AccountRow }>(
    activeId ? `/api/accounts/${activeId}` : null
  );
  const active = bundle?.account ?? null;

  // Click-outside + Escape close, mounted only while the dropdown is open.
  useEffect(() => {
    if (!pickerOpen) return;
    const onClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setPickerOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setPickerOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [pickerOpen]);

  return (
    <div ref={wrapRef} className="relative">
      <div className={cn(
        "flex items-center rounded-md border text-sm transition-colors",
        activeId
          ? "border-cta/40 bg-cta/10"
          : "border-warning/50 bg-warning/10 animate-pulse hover:animate-none"
      )}>
        {activeId ? (
          <>
            {/* The chip opens the current account's page — context in, context out. */}
            {(() => {
              // Same lead-with-company convention as the picker below: the
              // business name is what you're actually looking for, and the
              // record's own `name` can lag behind it (e.g. after a merge).
              const company = (active?.company ?? "").trim();
              const hasCompany = !!active && !!company && company.toLowerCase() !== active.name.trim().toLowerCase();
              const primary = active ? (hasCompany ? company : active.name) : "…";
              const secondary = active ? (hasCompany ? active.name : (businessOf(active) || active.city)) : null;
              return (
                <Hint tip="The account you are working on. Click to open its page. Account tools like Fulfillment and Client Portal follow this choice."><Link
                  to={`/accounts/${activeId}/overview`}
                  className="flex items-center gap-2 px-3 py-1.5"
                  aria-label={`Open account: ${primary}`}
                >
                  <Building className="size-3.5 text-cta" />
                  <span className="max-w-[220px] truncate font-medium">{primary}</span>
                  {secondary && (
                    <span className="hidden max-w-[160px] truncate text-xs text-muted-foreground sm:inline">· {secondary}</span>
                  )}
                </Link></Hint>
              );
            })()}
            {/* The chevron is the ONE account switcher. */}
            <Hint tip="Switch to a different account. You can search by name, city or owner."><button
              onClick={() => setPickerOpen((o) => !o)}
              className="border-l border-cta/40 px-1.5 py-1.5 text-muted-foreground hover:text-foreground"
              aria-label="Switch account"
              aria-haspopup="listbox"
              aria-expanded={pickerOpen}
            >
              <ChevronDown className={cn("size-3.5 transition-transform", pickerOpen && "rotate-180")} />
            </button></Hint>
            <Hint tip="Clear the selection. Nothing is deleted; you just stop working inside an account."><button
              onClick={() => setActiveAccountId(null)}
              className="border-l border-cta/40 px-2 py-1.5 text-muted-foreground hover:text-foreground"
              aria-label="Clear account context"
            >
              <X className="size-3.5" />
            </button></Hint>
          </>
        ) : (
          <Hint tip="Pick the customer or prospect you are working on. Account pages and tools then open for that account."><button
            onClick={() => setPickerOpen((o) => !o)}
            className="flex items-center gap-2 px-3 py-1.5"
            aria-label="Select an account"
            aria-haspopup="listbox"
            aria-expanded={pickerOpen}
          >
            <AlertTriangle className="size-3.5 text-warning" />
            <span className="text-xs font-medium">No account selected</span>
            <ChevronDown className={cn("size-3 text-muted-foreground transition-transform", pickerOpen && "rotate-180")} />
          </button></Hint>
        )}
      </div>
      {pickerOpen && (
        <AccountPicker
          activeId={activeId}
          onClose={() => setPickerOpen(false)}
          onPick={(id) => {
            setPickerOpen(false);
            nav(`/accounts/${id}/overview`);
          }}
        />
      )}
    </div>
  );
}

function AccountPicker({ activeId, onClose, onPick }: {
  activeId: number | null; onClose: () => void; onPick: (id: number) => void;
}) {
  const [q, setQ] = useState("");
  // Debounce so a fast typist doesn't fire one request per keystroke.
  // The picker's usable within ~150ms of stopping typing.
  const [needle, setNeedle] = useState("");
  useEffect(() => {
    const id = setTimeout(() => setNeedle(q.trim()), 150);
    return () => clearTimeout(id);
  }, [q]);
  // Server-side search when a needle is present — otherwise the browse
  // list of most-recently-updated accounts. This is what fixes the
  // "only the first N accounts show up" bug: with the search sent to
  // the server, the picker finds an account by name no matter how many
  // are in the workspace or how old the record is.
  const url = needle
    ? `/api/accounts?visibility=switcher&q=${encodeURIComponent(needle)}`
    : "/api/accounts?visibility=switcher";
  const { data } = useApi<{ rows: AccountRow[]; total?: number; limit?: number }>(url);
  const rows = data?.rows ?? [];

  const filtered = useMemo(() => {
    // With no needle, keep closed accounts (lost / disqualified /
    // churned) out of the default browse list — same as the pipeline
    // board — but never re-filter server-searched results.
    if (!needle) return rows.filter((r) => !["lost", "disqualified", "churned"].includes(r.status));
    return rows;
  }, [needle, rows]);
  const total = data?.total ?? 0;
  // Nothing found: look again without the pipeline filter, so an account in a
  // pipeline hidden from the switcher (Settings → Pipelines) is still findable
  // and the user learns why it didn't show, instead of "No accounts match".
  const searchedEmpty = !!needle && !!data && rows.length === 0;
  const { data: hiddenData } = useApi<{ rows: AccountRow[] }>(searchedEmpty ? `/api/accounts?q=${encodeURIComponent(needle)}&limit=25` : null);
  const { data: pipes } = useApi<{ pipelines: { ghl_pipeline_id: string; name: string }[] }>(searchedEmpty ? "/api/pipelines" : null);
  const hidden = searchedEmpty ? hiddenData?.rows ?? [] : [];
  const pipeName = (id: string | null | undefined) => pipes?.pipelines.find((x) => x.ghl_pipeline_id === id)?.name ?? "its pipeline";
  const showingAllInTenant = !needle && filtered.length >= total;

  return (
    <div
      className="absolute left-0 top-full z-50 mt-1 w-[420px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-md border border-border bg-card text-card-foreground shadow-xl"
      role="listbox"
    >
      <div className="flex items-center gap-2 border-b border-border px-3 py-2">
        <Search className="size-4 text-muted-foreground" />
        <input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search accounts by name, city, or owner…"
          className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
        />
        <button onClick={onClose} aria-label="Close" className="text-muted-foreground hover:text-foreground">
          <X className="size-4" />
        </button>
      </div>
      <ul className="max-h-[60vh] overflow-y-auto py-1">
        {filtered.length === 0 && hidden.length === 0 && (
          <li className="px-4 py-8 text-center text-sm text-muted-foreground">
            No accounts match "{q}".
          </li>
        )}
        {hidden.length > 0 && (
          <li className="px-4 pb-1 pt-2 text-xs text-muted-foreground">
            <span className="font-semibold uppercase tracking-wider">Hidden by pipeline settings</span>
            <span className="block">
              {[...new Set(hidden.map((r) => pipeName(r.ghl_pipeline_id)))].join(", ")} {new Set(hidden.map((r) => r.ghl_pipeline_id)).size === 1 ? "has" : "have"} the account switcher turned off.{" "}
              <Link to="/settings?tab=data" onClick={onClose} className="text-primary underline">Change in Settings</Link>
            </span>
          </li>
        )}
        {[...filtered, ...hidden].map((r) => (
          <li key={r.id}>
            <button
              onClick={() => onPick(r.id)}
              className={cn(
                "flex w-full items-center gap-3 px-4 py-2 text-left hover:bg-accent/30",
                r.id === activeId && "bg-accent/40"
              )}
            >
              <Building className="size-4 text-muted-foreground" />
              {(() => {
                // Lead with the company name when it's distinct from the account's own
                // `name` — that `name` can lag behind (e.g. after a merge) and searching
                // for the business only to see an old name back is exactly what confused
                // someone looking for "Complete Auto Care" and only finding "Ingrida".
                const company = (r.company ?? "").trim();
                const hasCompany = !!company && company.toLowerCase() !== r.name.trim().toLowerCase();
                const primary = hasCompany ? company : r.name;
                const owner = (r.owner_name ?? "").trim();
                const hasOwner = !!owner && owner.toLowerCase() !== primary.trim().toLowerCase();
                const subParts = [hasCompany ? r.name : null, !hasCompany && hasOwner ? owner : null, r.city].filter(Boolean) as string[];
                return (
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{primary}</p>
                    {subParts.length > 0 ? (
                      <p className="flex items-center gap-1.5 truncate text-xs text-muted-foreground">
                        {subParts.map((part, i) => (
                          <span key={i} className="flex items-center gap-1.5">
                            {i > 0 && <span>·</span>}
                            {part === r.city ? <span className="flex items-center gap-0.5"><MapPin className="size-3" />{part}</span> : <span className="truncate">{part}</span>}
                          </span>
                        ))}
                      </p>
                    ) : (
                      <p className="truncate text-xs italic text-muted-foreground/60">No business/city on file</p>
                    )}
                  </div>
                );
              })()}
              <span className="text-[10px] uppercase text-muted-foreground">{r.status}</span>
            </button>
          </li>
        ))}
      </ul>
      <div className="flex items-center justify-between gap-2 border-t border-border bg-muted/30 px-3 py-2 text-[11px] text-muted-foreground">
        <div className="flex items-center gap-3">
          <Link to="/pipeline" onClick={onClose} className="hover:text-foreground">Open Pipeline →</Link>
          <span>Esc to close</span>
        </div>
        <span className="truncate">
          {needle
            ? `${filtered.length + hidden.length} match${filtered.length + hidden.length === 1 ? "" : "es"} for "${needle}"`
            : total > 0
              ? (showingAllInTenant
                  ? `All ${total} accounts`
                  : `${filtered.length} recent · ${total} total — search to find older ones`)
              : ""}
        </span>
      </div>
    </div>
  );
}
